import { useSimulation } from '../engine/useSimulation';
import type { OrgConfig } from '../engine/types';
import { SentimentMeter } from './SentimentMeter';
import { PersonaPicker } from './PersonaPicker';
import { HoldSilence } from './HoldSilence';
import { SimulationSummary } from './SimulationSummary';
import { Chooser, personaChoices, objectionChoices } from './Chooser';
import { SummativeReview } from './SummativeReview';
import { Speaker } from './Speaker';

/**
 * §19 — the customer line as a distinct top block, options as large tappable
 * cards, the meter top-right with no numerals. One screen, no scrolling on a
 * phone during a turn, and no progress bar: knowing how many turns remain
 * telegraphs the close.
 */
export function SimulationRunner({
  orgId, scenarioId, config, uid, salesRole, seed, onDone,
}: {
  orgId: string;
  scenarioId: string;
  config: OrgConfig;
  uid: string;
  salesRole: string | null;
  seed?: number;
  onDone: () => void;
}) {
  const sim = useSimulation({ orgId, scenarioId, config, uid, salesRole, seed });
  const {
    scenario, phase, step, error, persona, node, line, audio, options, briefing,
    turns, displayed, band, start, select, tapExplain, revealing,
    readPersona, personaRead, objectionReads, outcome, outcomePassed, closeBand,
    classifyPersona, revisePersona, classifyObjection,
    summative, serverResult, holdMs,
  } = sim;

  if (phase === 'error') {
    return (
      <main className="centered">
        <div className="card">
          <h1>Something went wrong</h1>
          <p className="error">{error}</p>
          <button className="primary" onClick={onDone}>Back to your progress</button>
        </div>
      </main>
    );
  }

  if (phase === 'loading' || !scenario) {
    return <main className="centered"><p className="muted">Loading…</p></main>;
  }

  // §5.5 — a summative attempt runs with the persona assigned from the seed.
  // The rep is never offered a choice.
  if (phase === 'choosing-persona') {
    // A summative attempt starts itself from an effect in the hook; this is the
    // frame in between.
    if (summative) return <main className="centered"><p className="muted">Connecting…</p></main>;
    return <PersonaPicker scenario={scenario} config={config} onStart={start} onCancel={onDone} />;
  }

  if (phase === 'ended' && summative) {
    if (!serverResult) {
      return (
        <main className="centered">
          <div className="card">
            <h1>Call ended</h1>
            <p className="muted">
              Your answers have been submitted. The score is calculated on the
              server, and it is the only one that counts.
            </p>
          </div>
        </main>
      );
    }
    return (
      <SummativeReview
        result={serverResult as never}
        config={config}
        onDone={onDone}
      />
    );
  }

  /* §10.9 — the rep has closed and the prospect has not answered yet. Held
     before the reply renders, and before the summary on a close that ends the
     call, so the last thing a rep practises is the wait rather than the score. */
  if (phase === 'holding') {
    return <HoldSilence ms={holdMs} said={turns[turns.length - 1]?.optionText ?? null} />;
  }

  if (phase === 'ended') {
    return (
      <SimulationSummary
        scenario={scenario} turns={turns} band={band} value={displayed}
        meter={config.meter} persona={persona} onDone={onDone}
        onAgain={() => window.location.reload()}
        revealing={revealing}
        outcome={outcome} outcomePassed={outcomePassed} closeBand={closeBand}
        personaRead={personaRead} objectionReads={objectionReads}
        personas={config.personas}
      />
    );
  }

  const last = turns[turns.length - 1];
  const lastIndex = turns.length - 1;

  return (
    <main className="sim">
      <header className="sim-head">
        <button className="ghost small" onClick={onDone}>Leave</button>
        <SentimentMeter
          value={displayed} band={band} meter={config.meter}
          hidden={scenario.mode === 'summative'}
        />
      </header>

      {/* §10.6 — the "why" sits with the choice just made, after the meter has
          moved. Practice only: mid-attempt explanations in summative would turn
          the meter into an answer key. */}
      {last && revealing && (
        <div className={`echo echo-${last.tier}`}>
          <p className="echo-said">
            You said: “{last.optionText}”
            <Speaker src={last.optionAudio} label="Play your line back" />
          </p>
          {last.explainTapped ? (
            <div className="echo-explain">
              <p>{last.explain.what}</p>
              <p className="better">{last.explain.better}</p>
            </div>
          ) : (
            <button className="ghost small" onClick={() => tapExplain(lastIndex)}>
              Why did that land like that?
            </button>
          )}
        </div>
      )}

      {/* §4.3 — the closer opens on somebody else's account of the call. It is
          deliberately not marked true or false: deciding that is the exercise. */}
      {briefing && (
        <section className="briefing">
          <h2 className="briefing-head">What the setter told you</h2>
          <p className="briefing-text">{briefing}</p>
        </section>
      )}

      <section className="customer">
        <div className="customer-head">
          <span className="speaker">Them</span>
          <Speaker src={audio} label="Play what they said" />
        </div>
        <p className="customer-line">{line}</p>
      </section>

      {step === 'persona' && (
        <Chooser
          prompt="Who are you talking to?"
          hint="Name them before you decide what to say. You get one chance to revise this later."
          choices={personaChoices(config.personas, sim.personaChoices)}
          onChoose={classifyPersona}
        />
      )}

      {step === 'revision' && readPersona && (
        <Chooser
          prompt="Still reading them the same way?"
          hint="This is your one revision. Changing to the right read earns credit; changing away from it costs."
          choices={personaChoices(config.personas, sim.personaChoices)}
          onChoose={revisePersona}
          footer={
            <button className="ghost wide" onClick={() => revisePersona(null)}>
              Stick with {config.personas.find((p) => p.id === readPersona)?.name}
            </button>
          }
        />
      )}

      {step === 'objection' && (
        <Chooser
          prompt="What are they objecting to?"
          hint="Name the category. Get it wrong and you will be handling something they never raised."
          choices={objectionChoices(sim.objectionChoices)}
          onChoose={classifyObjection}
        />
      )}

      {step === 'options' && (
        <ul className="choices">
          {options.map((option) => (
            <li key={option.id}>
              <button className="choice" onClick={() => select(option.id)}>
                {option.text}
              </button>
            </li>
          ))}
        </ul>
      )}

      {node?.isPostClose && (
        <p className="small muted">You are past the decision now — this is the handoff.</p>
      )}
    </main>
  );
}
